/**
 * Tagesverlauf Control Components
 *
 * UI controls for the Tagesverlauf: banners, bulk actions, status buttons.
 */

import React from "react";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import {
  Clock,
  Save,
  Check,
  X,
  ChevronDown,
  Trash2,
  Backpack,
} from "lucide-react";
import { type TimelineLesson } from "./shared-hooks";

// ============================================================================
// ExcursionBanner - Shows when class is on excursion/trip
// ============================================================================

export interface ExcursionBannerProps {
  lesson: TimelineLesson;
  tripAttendanceStatus: string | null;
  savingTripAttendance: boolean;
  onTripAttendanceChange: (status: string) => void;
}

export function ExcursionBanner({
  lesson,
  tripAttendanceStatus,
  savingTripAttendance,
  onTripAttendanceChange,
}: ExcursionBannerProps) {
  const effectiveStatus = tripAttendanceStatus || lesson.status;
  const statusLabel =
    effectiveStatus === "present"
      ? "Anwesend"
      : effectiveStatus === "late"
      ? "Verspätet"
      : effectiveStatus === "absent_excused"
      ? "Informiert"
      : effectiveStatus === "absent_unexcused"
      ? "Abwesend (U)"
      : effectiveStatus === "absent_unclear"
      ? "Ungeklärt (U)"
      : "Nicht erfasst";
  const statusColor =
    effectiveStatus === "present"
      ? "bg-green-100 text-green-700 border-green-300"
      : effectiveStatus === "late"
      ? "bg-yellow-100 text-yellow-700 border-yellow-300"
      : effectiveStatus === "absent_excused"
      ? "bg-blue-100 text-blue-700 border-blue-300"
      : effectiveStatus === "absent_unexcused"
      ? "bg-red-100 text-red-700 border-red-300"
      : effectiveStatus === "absent_unclear"
      ? "bg-orange-100 text-orange-700 border-orange-300"
      : "bg-gray-100 text-gray-600 border-gray-300";

  return (
    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
      <div className="flex items-center gap-2">
        <Backpack className="h-5 w-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-medium text-amber-800">
            {lesson.classAbsenceType === "klassenfahrt"
              ? "Klassenfahrt"
              : lesson.classAbsenceType === "ausflug"
              ? "Ausflug"
              : lesson.classAbsenceType === "sonstiges"
              ? "Sonstiges"
              : "Klassenausflug"}
            {lesson.classAbsenceDestination &&
              ` – ${lesson.classAbsenceDestination}`}
          </div>
          <div className="text-xs text-amber-600">
            {lesson.classAbsenceStartTime && lesson.classAbsenceEndTime
              ? `${lesson.classAbsenceStartTime} – ${lesson.classAbsenceEndTime}`
              : "Ganztägig"}
            {lesson.classAbsenceAttendanceRequired === false &&
              " • Keine Anwesenheitspflicht"}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={savingTripAttendance}
              className={`text-xs px-2 py-1 rounded font-medium border flex items-center gap-1 ${statusColor} ${
                savingTripAttendance ? "opacity-50" : "hover:opacity-80"
              }`}
            >
              {statusLabel}
              <ChevronDown className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onTripAttendanceChange("present")}>
              <Check className="h-4 w-4 text-green-600 mr-2" /> Anwesend
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onTripAttendanceChange("late")}>
              <Clock className="h-4 w-4 text-yellow-600 mr-2" /> Verspätet
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onTripAttendanceChange("absent_excused")}
            >
              <X className="h-4 w-4 text-blue-600 mr-2" /> Informiert
            </DropdownMenuItem>
            {/* absent_unclear epic — Option B: trip "U" writes the
                pre-klärung state. Prüfung is the only path to formal
                absent_unexcused. */}
            <DropdownMenuItem
              onClick={() => onTripAttendanceChange("absent_unclear")}
            >
              <X className="h-4 w-4 text-orange-600 mr-2" /> Ungeklärt (U)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// ============================================================================
// BulkActionBar - Bulk status actions + save button
// ============================================================================

export interface BulkActionBarProps {
  lessons: TimelineLesson[];
  hasChanges: boolean;
  saving: boolean;
  deleting: boolean;
  showDeleteButton: boolean;
  isSuperAdmin: boolean;
  dateIso?: string;
  isSecondClickReady: (status: string) => boolean;
  setAllStatus: (status: string) => void;
  setShowDeleteConfirm: (show: boolean) => void;
  saveChanges: () => void;
  /** When true, the bar's inline Speichern button is suppressed.
   *  The bulk-status buttons still work — only the save UI is gone.
   *  Used by parent dialogs that own a single commit gate. */
  hideSaveButton?: boolean;
  /** Per-status label overrides from the day's top-level note status
   *  (`buildTagesverlaufStatusOverrides`). When present, the "Alle
   *  informiert" button reflects the resolved label — e.g. "Informiert
   *  (Krankgemeldet)" — so the bulk action matches the per-lesson
   *  pills. */
  statusLabelOverrides?: Record<string, { label: string; color: string }>;
}

export function BulkActionBar({
  lessons,
  hasChanges,
  saving,
  deleting,
  showDeleteButton,
  isSuperAdmin,
  dateIso,
  isSecondClickReady,
  setAllStatus,
  setShowDeleteConfirm,
  saveChanges,
  hideSaveButton = false,
  statusLabelOverrides,
}: BulkActionBarProps) {
  // When the day's top status is set (Krankgemeldet / Beurlaubt), the
  // bulk "Informiert" button mirrors the per-lesson pill label, e.g.
  // "Informiert (Krankgemeldet)". Falls back to plain "Alle informiert".
  const informiertLabel =
    statusLabelOverrides?.absent_excused?.label ?? "Alle informiert";
  // Bulk actions only target past+current lessons — see paired guard
  // in useTagesverlauf.setAllStatus. Future lessons are excluded
  // from the counts the tooltips report so the numbers match what
  // will actually change.
  const now = Date.now();
  const lessonDateIso = dateIso ? dateIso.slice(0, 10) : "";
  const isFutureLesson = (l: TimelineLesson) => {
    if (!lessonDateIso || !l.start) return false;
    const ts = new Date(`${lessonDateIso}T${l.start}`).getTime();
    return Number.isFinite(ts) && ts > now;
  };
  const eligibleForBulk = lessons.filter(
    (l) => l.lesson_id && !isFutureLesson(l),
  );
  const emptyCount = eligibleForBulk.filter(
    (l) =>
      !l.status || l.status === "nicht_eingetragen" || l.status === "",
  ).length;
  const totalCount = eligibleForBulk.length;

  return (
    <div className="space-y-1">
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        variant="outline"
        size="sm"
        className={`text-green-700 border-green-300 hover:bg-green-50 ${
          isSecondClickReady("present")
            ? "ring-2 ring-green-400 bg-green-50"
            : ""
        }`}
        onClick={() => setAllStatus("present")}
        title={
          isSecondClickReady("present")
            ? `Nochmal klicken: Alle ${totalCount} überschreiben`
            : emptyCount > 0
            ? `${emptyCount} leere Stunden setzen`
            : `Alle ${totalCount} überschreiben`
        }
      >
        <Check className="h-3 w-3 mr-1" />
        Alle anwesend
      </Button>
      {/* "Informiert" semantically maps to absent_excused at the data
          layer — the teacher has been informed of the absence, so it's
          treated as excused. Label updated to match the per-lesson
          "Inf" button + dialog's Informiert column. */}
      <Button
        variant="outline"
        size="sm"
        className={`text-blue-700 border-blue-300 hover:bg-blue-50 ${
          isSecondClickReady("absent_excused")
            ? "ring-2 ring-blue-400 bg-blue-50"
            : ""
        }`}
        onClick={() => setAllStatus("absent_excused")}
        title={
          isSecondClickReady("absent_excused")
            ? `Nochmal klicken: Alle ${totalCount} überschreiben`
            : emptyCount > 0
            ? `${emptyCount} leere Stunden setzen`
            : `Alle ${totalCount} überschreiben`
        }
      >
        {informiertLabel}
      </Button>
      {/* absent_unclear epic — Option B: bulk "U" writes the
          pre-klärung state. Formal absent_unexcused only ever comes
          from a Prüfung outcome of endgueltig_unentschuldigt. */}
      <Button
        variant="outline"
        size="sm"
        className={`text-orange-700 border-orange-300 hover:bg-orange-50 ${
          isSecondClickReady("absent_unclear")
            ? "ring-2 ring-orange-400 bg-orange-50"
            : ""
        }`}
        onClick={() => setAllStatus("absent_unclear")}
        title={
          isSecondClickReady("absent_unclear")
            ? `Nochmal klicken: Alle ${totalCount} überschreiben`
            : emptyCount > 0
            ? `${emptyCount} leere Stunden setzen`
            : `Alle ${totalCount} überschreiben`
        }
      >
        <X className="h-3 w-3 mr-1" />
        Alle ungeklärt (U)
      </Button>
      <div className="flex-1" />
      {showDeleteButton && isSuperAdmin && dateIso && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={deleting}
        >
          <Trash2 className="h-3 w-3 mr-1" />
          {deleting ? "Löschen..." : "Eintrag löschen"}
        </Button>
      )}
      {hasChanges && !hideSaveButton && (
        <Button size="sm" onClick={saveChanges} disabled={saving}>
          <Save className="h-3 w-3 mr-1" />
          {saving ? "Speichern..." : "Speichern"}
        </Button>
      )}
    </div>
      {/* The bulk buttons set only empty lessons on the first click;
          a second click (button highlighted) overwrites every lesson,
          including already-set ones. */}
      <p className="text-xs text-muted-foreground">
        Tipp: Zweimal klicken überschreibt auch bereits gesetzte Stunden.
      </p>
    </div>
  );
}

// ============================================================================
// QuickStatusButtons - For not-yet-entered lessons
// ============================================================================

export interface QuickStatusButtonsProps {
  lessonId: string;
  changes: Record<string, string>;
  openLatenessPopover: string | null;
  setOpenLatenessPopover: (id: string | null) => void;
  setLessonStatus: (lessonId: string, status: string) => void;
}

export function QuickStatusButtons({
  lessonId,
  changes,
  openLatenessPopover,
  setOpenLatenessPopover,
  setLessonStatus,
}: QuickStatusButtonsProps) {
  const isChanged = changes[lessonId] !== undefined;

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => setLessonStatus(lessonId, "present")}
        className={`w-7 h-7 rounded flex items-center justify-center border transition-all hover:scale-110 ${
          isChanged && changes[lessonId] === "present"
            ? "bg-green-500 text-white border-green-600"
            : "bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100"
        }`}
        title="Anwesend"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() =>
          setOpenLatenessPopover(
            openLatenessPopover === lessonId ? null : lessonId,
          )
        }
        className={`w-7 h-7 rounded flex items-center justify-center border transition-all hover:scale-110 ${
          isChanged && changes[lessonId] === "late"
            ? "bg-yellow-500 text-white border-yellow-600"
            : "bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100"
        }`}
        title="Verspätet"
      >
        <Clock className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => setLessonStatus(lessonId, "absent_excused")}
        className={`w-7 h-7 rounded flex items-center justify-center border font-bold text-[10px] transition-all hover:scale-110 ${
          isChanged && changes[lessonId] === "absent_excused"
            ? "bg-blue-500 text-white border-blue-600"
            : "bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100"
        }`}
        title="Informiert"
        aria-label="Informiert"
      >
        Inf
      </button>
      {/* absent_unclear epic — Option B: lesson-level "U" writes the
          pre-klärung state (orange). Formal absent_unexcused (red) is
          unreachable from the timeline; only Prüfung produces it. */}
      <button
        type="button"
        onClick={() => setLessonStatus(lessonId, "absent_unclear")}
        className={`w-7 h-7 rounded flex items-center justify-center border font-bold text-xs transition-all hover:scale-110 ${
          isChanged && changes[lessonId] === "absent_unclear"
            ? "bg-orange-500 text-white border-orange-600"
            : "bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100"
        }`}
        title="Ungeklärt abwesend"
      >
        U
      </button>
    </div>
  );
}

// ============================================================================
// StatusDropdown - For already-entered lessons
// ============================================================================

export interface StatusDropdownProps {
  lessonId: string;
  label: string;
  color: string;
  isChanged: boolean;
  isDebugMode: boolean;
  status: string | null;
  setLessonStatus: (lessonId: string, status: string) => void;
  setOpenLatenessPopover: (id: string | null) => void;
  handleDeleteExcursionLessonAttendance: (lessonId: string) => void;
}

export function StatusDropdown({
  lessonId,
  label,
  color,
  isChanged,
  isDebugMode,
  status,
  setLessonStatus,
  setOpenLatenessPopover,
  handleDeleteExcursionLessonAttendance,
}: StatusDropdownProps) {
  return (
    <div className="flex items-center gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${color} ${
              isChanged ? "ring-2 ring-blue-400" : ""
            }`}
          >
            {label}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => setLessonStatus(lessonId, "present")}
          >
            <Check className="h-4 w-4 mr-2 text-green-600" />
            Anwesend
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setLessonStatus(lessonId, "absent_excused")}
          >
            <span className="w-6 h-4 mr-2 text-blue-600 font-bold text-center text-[10px]">
              Inf
            </span>
            Informiert
          </DropdownMenuItem>
          {/* absent_unclear epic — Option B: dropdown "U" writes
              pre-klärung state. Prüfung is the only path to formal
              absent_unexcused. */}
          <DropdownMenuItem
            onClick={() => setLessonStatus(lessonId, "absent_unclear")}
          >
            <span className="w-4 h-4 mr-2 text-orange-600 font-bold text-center">
              U
            </span>
            Ungeklärt
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setOpenLatenessPopover(lessonId)}>
            <Clock className="h-4 w-4 mr-2 text-yellow-600" />
            Verspätet
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {isDebugMode && status && (
        <button
          type="button"
          onClick={() => handleDeleteExcursionLessonAttendance(lessonId)}
          className="p-1 rounded hover:bg-red-100 text-red-500 hover:text-red-700"
          title="[DEBUG] Anwesenheitseintrag löschen"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// ============================================================================
// LatenessPopover - Inline popover for lateness details
// ============================================================================

export interface LatenessPopoverProps {
  lessonId: string;
  latenessDetails: Record<string, { minutes: number; excused: boolean }>;
  setLatenessDetails: React.Dispatch<
    React.SetStateAction<Record<string, { minutes: number; excused: boolean }>>
  >;
  setLatenessInfo: (
    lessonId: string,
    minutes: number,
    excused: boolean,
  ) => void;
  setOpenLatenessPopover: (id: string | null) => void;
}

export function LatenessPopover({
  lessonId,
  latenessDetails,
  setLatenessDetails,
  setLatenessInfo,
  setOpenLatenessPopover,
}: LatenessPopoverProps) {
  return (
    <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={45}
            placeholder="Min."
            value={latenessDetails[lessonId]?.minutes || ""}
            onChange={(e) => {
              const val = Math.min(
                45,
                Math.max(1, parseInt(e.target.value) || 0),
              );
              const currentInfo = latenessDetails[lessonId];
              setLatenessDetails((prev) => ({
                ...prev,
                [lessonId]: {
                  minutes: val,
                  excused: currentInfo?.excused ?? false,
                },
              }));
            }}
            className="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          <span className="text-sm text-gray-600">Min. verspätet</span>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={latenessDetails[lessonId]?.excused ?? false}
            onChange={(e) => {
              const currentInfo = latenessDetails[lessonId];
              setLatenessDetails((prev) => ({
                ...prev,
                [lessonId]: {
                  minutes: currentInfo?.minutes || 0,
                  excused: e.target.checked,
                },
              }));
            }}
            className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
          />
          <span className="text-sm text-gray-700">Entschuldigt</span>
        </label>

        <button
          type="button"
          onClick={() => {
            const info = latenessDetails[lessonId];
            if (info?.minutes) {
              setLatenessInfo(lessonId, info.minutes, info.excused);
            }
          }}
          disabled={!latenessDetails[lessonId]?.minutes}
          className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          OK
        </button>

        <button
          type="button"
          onClick={() => setOpenLatenessPopover(null)}
          className="text-gray-400 hover:text-gray-600 ml-auto"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
